import LayoutRendererShape, {
    fromLayoutShape,
    LayoutRendererReactComponent,
    toLayoutShape,
} from './LayoutRendererComponent';
import LayoutShape from '../layout/LayoutShape';
import { Store } from 'redux';
import Services from '../store/Services';
import GoldenLayout from 'golden-layout';
import { WidgetsService } from '../widgets/WidgetsService';
import isNil from 'lodash/isNil';

export interface LayoutRendererConfig {
    settings: {
        showPopoutIcon: boolean;
        showMaximiseIcon: boolean;
        showCloseIcon: boolean;
    };
    dimensions: {
        borderWidth: number;
        headerHeight: number;
        minItemWidth: number;
        minItemHeight: number;
    };
    labels: {
        close: string;
    };
    content: Array<LayoutRendererShape>;
}

export function configFromProps(
    store: Store,
    services: Services,
    layoutShape: LayoutShape | undefined
): LayoutRendererConfig {
    return {
        settings: {
            showPopoutIcon: false,
            showMaximiseIcon: false,
            showCloseIcon: false,
        },
        dimensions: {
            borderWidth: 8,
            headerHeight: 24,
            minItemWidth: 200,
            minItemHeight: 100,
        },
        labels: {
            close: 'close',
        },
        content: layoutShape
            ? [fromLayoutShape(layoutShape, store, services)]
            : [],
    };
}

export function shapefromConfig(
    config: LayoutRendererConfig
): LayoutShape | undefined {
    return config.content && config.content.length > 0 && config.content[0]
        ? toLayoutShape(config.content[0])
        : undefined;
}

export function findFirstActiveInput(
    c: GoldenLayout.ContentItem,
    widgetsService: WidgetsService
) {
    const activeInputs: GoldenLayout.ContentItem[] = [];
    lookupActiveInputs(c, widgetsService, (ai: GoldenLayout.ContentItem) => {
        activeInputs.push(ai);
    });
    return activeInputs[0];
}

export function lookupActiveInputs(
    c: GoldenLayout.ContentItem,
    widgetsService: WidgetsService,
    onActiveInputVisited: (c: GoldenLayout.ContentItem) => void
) {
    if (!c) {
        return undefined;
    }
    if (c.type === 'component') {
        const config = c.config as LayoutRendererReactComponent;
        if (
            widgetsService.isInput(config.props.componentType) &&
            c.parent.getActiveContentItem &&
            (
                c.parent.getActiveContentItem()
                    .config as GoldenLayout.ReactComponentConfig
            ).props.componentId === config.props.componentId
        ) {
            onActiveInputVisited(c);
        }
    }
    if (c.contentItems) {
        c.contentItems.forEach((ci) =>
            lookupActiveInputs(ci, widgetsService, onActiveInputVisited)
        );
    }
}

export function hasCurrentLayoutChanged(
    prevLayoutId: number | null | undefined,
    nextLayoutId: number | null | undefined
): boolean {
    if (isNil(prevLayoutId) && !isNil(nextLayoutId)) {
        // Initial save => treat as same layout
        return false;
    }
    return prevLayoutId !== nextLayoutId;
}

export async function ensureTopItemIsRow(
    gl: GoldenLayout
): Promise<GoldenLayout.ContentItem> {
    let topLevelItem = gl.root.contentItems[0];
    if (!topLevelItem) {
        gl.root.addChild({
            type: 'row',
            content: [],
        });
        topLevelItem = gl.root.contentItems[0];
    } else if (topLevelItem.config.type !== 'row') {
        const config = extractTopLevelConfig(gl);

        await new Promise((resolve) => {
            gl.root.removeChild(topLevelItem as GoldenLayout.ItemConfig);
            setTimeout(resolve, 0);
        });
        await new Promise((resolve) => {
            gl.root.addChild({
                type: 'row',
                content: [config],
            });
            setTimeout(resolve, 0);
        });

        topLevelItem = gl.root.contentItems[0];
    }

    return topLevelItem;
}

export function extractTopLevelConfig(gl: GoldenLayout) {
    const config: LayoutRendererConfig = gl.toConfig();
    return config.content[0];
}

export function isDragging() {
    let node: ChildNode | null = document.body.lastChild;
    while (node) {
        if (
            (node as HTMLElement).className &&
            (node as HTMLElement).className === 'lm_dragProxy'
        ) {
            return true;
        }
        node = node.previousSibling as ChildNode | null;
    }
    return false;
}
